import type { ProviderConfig } from "@mariozechner/pi-coding-agent";
import { readFileSync } from "node:fs";
import { runNeuroSkill, discoverSkillServer, getSkillPort, getDaemonTokenPath } from "./neuroskill/index.ts";

function loadToken(): string {
	try { return readFileSync(getDaemonTokenPath(), "utf8").trim(); }
	catch { return ""; }
}

export function authHeaders(): Record<string, string> {
	const token = loadToken();
	return token ? { Authorization: `Bearer ${token}` } : {};
}

interface ProviderRegistry {
	registerProvider: (id: string, cfg: ProviderConfig) => void;
}

function localModelEntry(
	id: string,
	opts: { contextWindow?: number; supportsVision?: boolean } = {},
) {
	return {
		id,
		name: id,
		reasoning: true,
		input: (opts.supportsVision ? ["text", "image"] : ["text"]) as ("text" | "image")[],
		cost: { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 },
		contextWindow: opts.contextWindow ?? 32768,
		maxTokens: 8192,
		compat: {
			supportsStore: false,
			supportsReasoningEffort: false,
			supportsDeveloperRole: false,
			requiresToolResultName: false,
			supportsStrictMode: false,
		},
	};
}

export async function registerSkillLlmProvider(modelRegistry: ProviderRegistry): Promise<boolean> {
	try {
		const discoveredPort = await discoverSkillServer();
		if (!discoveredPort) return false;
		const baseUrl = `http://127.0.0.1:${discoveredPort}`;
		const hdrs = authHeaders();

		const models: ReturnType<typeof localModelEntry>[] = [];
		let serverRunning = false;

		// Try to get LLM server status (running model info)
		for (const path of ["/llm/status", "/v1/llm/server/status"]) {
			try {
				const r = await fetch(`${baseUrl}${path}`, { signal: AbortSignal.timeout(2000), headers: hdrs });
				if (r.ok) {
					const status = (await r.json()) as {
						status: string; model?: string; model_name?: string;
						n_ctx?: number; supports_vision?: boolean;
					};
					if (status.status === "running" || status.status === "ok") {
						const name = status.model_name ?? status.model;
						if (name) {
							models.push(localModelEntry(name, {
								contextWindow: status.n_ctx ?? 32768,
								supportsVision: status.supports_vision ?? false,
							}));
							serverRunning = true;
						}
					}
					break;
				}
			} catch { /* try next */ }
		}

		// Fetch catalog to list all downloaded models (even if server isn't running)
		try {
			const catRes = await fetch(`${baseUrl}/v1/llm/catalog`, {
				signal: AbortSignal.timeout(2000), headers: hdrs,
			});
			if (catRes.ok) {
				const cat = (await catRes.json()) as {
					active_model?: string;
					entries?: Array<{ filename?: string; state?: string; is_mmproj?: boolean }>;
				};
				const existing = new Set(models.map((m) => m.id));
				// If server isn't running, add the active model first
				if (!serverRunning && cat.active_model) {
					const active = (cat.entries ?? []).find(
						(e) => e.filename === cat.active_model && e.state === "downloaded" && !e.is_mmproj,
					);
					if (active?.filename && !existing.has(active.filename)) {
						models.push(localModelEntry(active.filename));
						existing.add(active.filename);
					}
				}
				for (const e of cat.entries ?? []) {
					if (!e.filename || e.is_mmproj) continue;
					if (e.state !== "downloaded") continue;
					if (!existing.has(e.filename)) {
						models.push(localModelEntry(e.filename));
						existing.add(e.filename);
					}
				}
			}
		} catch { /* ignore */ }

		if (models.length === 0) return false;

		modelRegistry.registerProvider("skill-llm", {
			baseUrl: `${baseUrl}/v1`,
			apiKey: loadToken() || "SKILL_LLM_API_KEY",
			api: "openai-completions",
			models,
		});
		return true;
	} catch {
		return false;
	}
}

export async function startSkillLlmServer(mode: "local" | "remote" | "auto" = "auto"): Promise<{ ok: boolean; message: string }> {
	const localStart = async () => runNeuroSkill(["llm", "start"]);
	const remoteAttempts = [
		{ command: "llm_start", args: { mode: "remote" } },
		{ command: "llm_start", args: { remote: true } },
		{ command: "llm_start", args: { backend: "remote" } },
	];

	const tryRemote = async () => {
		for (const payload of remoteAttempts) {
			const r = await runNeuroSkill(["raw", JSON.stringify(payload)]);
			if (r.ok) return r;
		}
		return { ok: false, error: "remote llm_start ws command not supported" };
	};

	if (mode === "local") {
		const r = await localStart();
		return { ok: r.ok, message: r.ok ? "Skill LLM local server started." : (r.error ?? "Failed to start local server") };
	}

	if (mode === "remote") {
		const r = await tryRemote();
		if (r.ok) return { ok: true, message: "Skill LLM remote server started via WS." };
		return { ok: false, message: r.error ?? "Failed to start remote server" };
	}

	const remote = await tryRemote();
	if (remote.ok) return { ok: true, message: "Skill LLM remote server started via WS." };
	const local = await localStart();
	if (local.ok) return { ok: true, message: "Remote start unavailable; local llama.cpp server started." };
	return { ok: false, message: local.error ?? "Failed to start Skill LLM server" };
}

export async function autoBootSkillLlmIfConfigured(): Promise<void> {
	const raw = (process.env.NEUROLOOP_SKILL_LLM_BOOT ?? "off").toLowerCase();
	const mode = raw === "remote" || raw === "local" || raw === "auto" ? raw : "off";
	if (mode === "off") return;
	await startSkillLlmServer(mode);
}

export async function getSkillServerBaseUrl(): Promise<string> {
	const port = (await discoverSkillServer()) ?? getSkillPort();
	return `http://127.0.0.1:${port}`;
}
